/**
 * readCurrentVesselId.js
 *
 * FT-365 v3.1 hotfix helper.
 *
 * Background:
 *   FT-365 v3 Layer C routed 5 `currentVesselId` writers through `legacySet()`,
 *   which internally calls `JSON.stringify(value)` (via `lib/storage.writeJSON`).
 *   That double-encodes the raw UUID (e.g. stores `"aefc842b-..."` with embedded
 *   double-quotes) whereas ALL downstream readers use raw `localStorage.getItem`
 *   and expect a plain UUID. The mismatch caused the FT-64 vessel-guard at
 *   `src/utils/vesselGuard.js:105` to reject every vessel-scoped write, spraying
 *   `[VESSEL-GUARD] Blocked cross-vessel write` warnings tens of thousands of
 *   times per second and freezing the UI (~54k warnings in 30s during trial).
 *
 * FT-365 v3.1 Fix 1: revert the 5 writers back to raw `localStorage.setItem`.
 * FT-365 v3.1 Fix 2 (this file): defensively unwrap any double-encoded value
 * already sitting in the browser from a user who briefly touched v3 in prod,
 * and self-heal the storage in place so it only happens once per browser.
 *
 * All raw readers of `currentVesselId` should call `readCurrentVesselIdRaw()`
 * instead of `localStorage.getItem('currentVesselId')` directly.
 */

/* eslint-disable no-restricted-syntax -- FT-365 v3.1 storage-layer helper is
 * the canonical raw reader for currentVesselId. Downstream React components
 * import readCurrentVesselIdRaw() rather than touching localStorage directly,
 * so the ban is satisfied at the call-site level. */

const CURRENT_VESSEL_KEY = 'currentVesselId';

/**
 * Read `currentVesselId` from localStorage, transparently unwrapping the
 * FT-365 v3 legacy double-JSON encoding if present, and self-healing the
 * storage value in place so subsequent reads are clean.
 *
 * @returns {string|null} the raw UUID, or null if not set
 */
export function readCurrentVesselIdRaw() {
  let value = null;
  try {
    value = localStorage.getItem(CURRENT_VESSEL_KEY);
  } catch (_) {
    return null;
  }
  if (!value) return null;

  // FT-365 v3.1 self-heal: strip legacy double-JSON encoding if present.
  // A raw UUID never starts with `"`, so this is a safe signature check.
  if (value.length >= 2 && value.charCodeAt(0) === 34 /* " */ && value.charCodeAt(value.length - 1) === 34) {
    try {
      const unwrapped = JSON.parse(value);
      if (typeof unwrapped === 'string' && unwrapped.length > 0) {
        // Persist the corrected value so we only unwrap once per browser.
        try {
          // eslint-disable-next-line no-console -- FT-365 v3.1 one-shot observability: fires only on first read of a legacy value per browser session (self-heal makes subsequent reads clean).
          console.info('[FT-365 v3.1] Self-healed double-encoded currentVesselId in LS:', value, '→', unwrapped);
          localStorage.setItem(CURRENT_VESSEL_KEY, unwrapped);
        } catch (_) { /* non-fatal — next read will re-unwrap */ }
        return unwrapped;
      }
    } catch (_) {
      // JSON.parse failed — value was quoted but not valid JSON. Fall through
      // and return as-is; downstream comparisons will fail loudly rather than
      // silently.
    }
  }

  return value;
}

export default readCurrentVesselIdRaw;
